import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { wealthboxApi, queryKeys } from '@client/api/integrations';
import { useDebounce } from '@client/hooks/useDebounce';
import { Contacts } from '../components/integrations/Contacts';
import { LinkClientModal } from '../components/integrations/LinkClientModal';
import type { IntegrationContact } from '@client/api/types';
import type { Wealthbox } from '@client/api/types';

function normalizeWealthboxContact(contact: Wealthbox.Contact): IntegrationContact {
  return {
    id: String(contact.id),
    firstName: contact.firstName,
    lastName: contact.lastName,
    companyName: contact.companyName,
    email: contact.email,
    type: contact.type,
    status: contact.status,
    waterlilyClientId: contact.waterlilyClientId,
  };
}

export function AdvisorIntegrationsWealthboxContacts() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [contactToLink, setContactToLink] = useState<IntegrationContact | null>(null);
  const queryClient = useQueryClient();

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.wealthbox.list('contacts', {
      page,
      perPage: pageSize,
      query: debouncedSearch,
    }),
    queryFn: () =>
      wealthboxApi.contacts.list({
        page,
        perPage: pageSize,
        query: debouncedSearch || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const linkMutation = useMutation({
    mutationFn: ({ contactId, clientId }: { contactId: string; clientId: string }) =>
      wealthboxApi.contacts.link({ contactId, clientId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.wealthbox.lists('contacts') });
      setContactToLink(null);
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (contactId: string) => wealthboxApi.contacts.unlink(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.wealthbox.lists('contacts') });
    },
  });

  const contacts = (data?.contacts ?? []).map(normalizeWealthboxContact);
  const wealthboxAccountId = data?.wealthboxAccountId;
  const totalPages = data?.meta?.totalPages ?? 1;

  const getExternalUrl = wealthboxAccountId
    ? (contactId: string) => `https://app.crmworkspace.com/${wealthboxAccountId}/contacts/${contactId}`
    : undefined;

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <div className="my-7 flex flex-col items-center justify-between gap-8 md:flex-row md:gap-0">
        <div className="flex items-center gap-5">
          <h1 className="text-primary-dark text-4xl font-semibold">Wealthbox Contacts</h1>
        </div>
      </div>
      <div className="bg-primary-100 my-6 h-px w-full" />

      <Contacts
        contacts={contacts}
        totalPages={totalPages}
        isFetching={isFetching}
        error={error}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        search={search}
        onSearchChange={setSearch}
        onRefetch={refetch}
        onLink={setContactToLink}
        onUnlink={contactId => unlinkMutation.mutate(contactId)}
        isUnlinking={unlinkMutation.isPending}
        crmName="Wealthbox"
        getExternalUrl={getExternalUrl}
        integrationSource="wealthbox"
      />

      {!!contactToLink && (
        <LinkClientModal
          open={!!contactToLink}
          onClose={() => setContactToLink(null)}
          contact={contactToLink}
          onLink={(contactId, clientId) => linkMutation.mutate({ contactId, clientId })}
          isLinking={linkMutation.isPending}
          linkError={linkMutation.error}
          onReset={() => linkMutation.reset()}
        />
      )}
    </main>
  );
}
